/* TASK-0027: selected Shopify media only. Native HLS first; bounded compatibility fallback. */
(() => {
  if (!window.customElements || customElements.get('rawry-hero-video')) return;
  // David's approved visible-quality floor; higher Shopify renditions remain available.
  const MIN_VIDEO_HEIGHT = 720;
  const MANIFEST_TIMEOUT_MS = 10000;
  function qualityMaster(text, base) {
    const lines = text.trim().split(/\r?\n/);
    if (lines[0] !== '#EXTM3U') throw new Error('Invalid HLS master');
    const output = [];
    let variants = 0;
    const absolute = uri => new URL(uri, base).href;
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (line.startsWith('#EXT-X-STREAM-INF:')) {
        const height = Number(line.match(/RESOLUTION=\d+x(\d+)(?:,|$)/)?.[1]);
        const uri = lines[++i]?.trim();
        if (!uri || uri.startsWith('#')) throw new Error('Invalid HLS variant');
        if (height >= MIN_VIDEO_HEIGHT) {
          output.push(line, absolute(uri));
          variants++;
        }
      } else if (line.startsWith('#EXT-X-I-FRAME-STREAM-INF:')) {
        // The decorative player has no trick-play path.
        continue;
      } else if (line && !line.startsWith('#')) {
        throw new Error('Expected an HLS master');
      } else {
        output.push(line.replace(/URI="([^"]+)"/g, (_, uri) => `URI="${absolute(uri)}"`));
      }
    }
    if (!variants) throw new Error('No eligible HLS rendition');
    return output.join('\n') + '\n';
  }
  let hlsScript, ownedHls;
  function validHls(Hls) {
    return typeof Hls === 'function' && Hls.version === '1.7.3' && typeof Hls.isSupported === 'function';
  }
  function loadHls(url, signal) {
    if (signal.aborted) return Promise.reject(new Error('Section removed'));
    if (ownedHls) return Promise.resolve(ownedHls);
    return new Promise((resolve, reject) => {
      let request = hlsScript;
      if (request && request.url !== url) { reject(new Error('Different HLS request')); return; }
      if (!request) {
        const script = document.createElement('script');
        request = {url, script, waiters: new Set(), settled: false};
        hlsScript = request;
        request.finish = error => {
          if (request.settled) return;
          request.settled = true;
          clearTimeout(request.timer);
          script.onload = script.onerror = null;
          script.remove();
          if (hlsScript === request) hlsScript = null;
          // The pinned bundle exports only onto its own executing script element.
          const Hls = script.rawryHls;
          if (!error && !validHls(Hls)) error = new Error('Unavailable Rawry HLS export');
          if (!error) ownedHls = Hls;
          for (const waiter of request.waiters) {
            waiter.signal.removeEventListener('abort', waiter.cancel);
            if (error) waiter.reject(error); else waiter.resolve(Hls);
          }
          request.waiters.clear();
        };
        script.src = url;
        script.async = true;
        script.onload = () => request.finish();
        script.onerror = () => request.finish(new Error('HLS script failed'));
        request.timer = setTimeout(() => request.finish(new Error('HLS script timed out')), MANIFEST_TIMEOUT_MS);
      }
      const waiter = {resolve, reject, signal};
      waiter.cancel = () => {
        signal.removeEventListener('abort', waiter.cancel);
        request.waiters.delete(waiter);
        reject(new Error('Section removed'));
        if (!request.waiters.size) request.finish(new Error('No HLS consumers'));
      };
      request.waiters.add(waiter);
      signal.addEventListener('abort', waiter.cancel, {once: true});
      if (!request.script.isConnected) document.head.appendChild(request.script);
    });
  }
  class RawryHeroVideo extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.video = this.querySelector('video');
      const config = this.querySelector('[data-hero-config]');
      if (!this.video || !config) return;
      try { this.config = JSON.parse(config.textContent); } catch (_) { return; }
      this.abort = new AbortController();
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.userPaused = false;
      this.autoPausing = false;
      this.path = '';
      this.tried = new Set();
      this.generation = (this.generation || 0) + 1;
      this.inView = this.getBoundingClientRect().bottom > 0 && this.getBoundingClientRect().top < innerHeight;
      this.video.muted = true;
      this.video.autoplay = false; // Source attachment cannot bypass motion/visibility/user intent.
      const on = (target, event, handler) => target.addEventListener(event, handler, {signal: this.abort.signal});
      on(document, 'visibilitychange', () => this.sync());
      on(this.motion, 'change', () => this.sync());
      on(this.video, 'playing', () => {
        if (!this.wantsPlay()) { this.pause(); return; }
        const epoch = this.sourceEpoch;
        const reveal = () => {
          this.frameRequest = null;
          if (!this.abort || epoch !== this.sourceEpoch) return;
          if (this.video.videoHeight < MIN_VIDEO_HEIGHT) { this.nextPath(); return; }
          this.setAttribute('data-has-frame', '');
        };
        if (this.video.requestVideoFrameCallback) this.frameRequest = this.video.requestVideoFrameCallback(reveal);
        else reveal();
      });
      on(this.video, 'pause', () => {
        if (this.autoPausing) this.autoPausing = false;
        else if (this.path && !this.switching) this.userPaused = true;
      });
      on(this.video, 'error', () => { if (!this.switching) this.nextPath(); });
      on(document, 'shopify:section:unload', event => {
        if (event.target.contains(this)) this.disconnectedCallback();
      });
      this.observer = new IntersectionObserver(entries => {
        this.inView = entries[0].isIntersecting;
        this.sync();
      });
      this.observer.observe(this);
      this.sync();
    }
    wantsPlay() {
      return !this.failed && !this.userPaused && this.inView && !document.hidden;
    }
    pause() {
      if (!this.video.paused) { this.autoPausing = true; this.video.pause(); }
      this.hls?.stopLoad();
    }
    sync() {
      if (!this.abort) return;
      if (!this.wantsPlay()) { this.pause(); return; }
      if (!this.path) { this.nextPath(); return; }
      if (this.loading) return;
      this.hls?.startLoad();
      const sourceEpoch = this.sourceEpoch;
      this.video.play().catch(error => {
        if (!this.abort || sourceEpoch !== this.sourceEpoch || error.name === 'AbortError') return;
        if (error.name === 'NotAllowedError') {
          this.userPaused = true;
          this.removeAttribute('data-has-frame');
          this.pause();
        } else this.nextPath();
      });
    }
    async adaptiveSource() {
      if (!this.masterPromise) {
        const generation = this.generation;
        this.masterPromise = (async () => {
          const controller = new AbortController();
          const lifetime = this.abort.signal;
          const cancel = () => controller.abort();
          lifetime.addEventListener('abort', cancel, {once: true});
          const timeout = setTimeout(cancel, MANIFEST_TIMEOUT_MS);
          try {
            const response = await fetch(this.config.hls, {signal: controller.signal});
            if (!response.ok) throw new Error('HLS master unavailable');
            const master = qualityMaster(await response.text(), response.url);
            if (!this.abort || this.generation !== generation) throw new Error('Section removed');
            this.masterUrl = URL.createObjectURL(new Blob([master], {type: 'application/vnd.apple.mpegurl'}));
            return this.masterUrl;
          } finally {
            clearTimeout(timeout);
            lifetime.removeEventListener('abort', cancel);
          }
        })();
      }
      return this.masterPromise;
    }
    clearSource() {
      this.sourceEpoch = (this.sourceEpoch || 0) + 1;
      this.switching = true;
      if (this.frameRequest && this.video.cancelVideoFrameCallback) this.video.cancelVideoFrameCallback(this.frameRequest);
      this.frameRequest = null;
      this.pause();
      this.hls?.destroy();
      this.hls = null;
      this.video.removeAttribute('src');
      this.video.load();
      this.removeAttribute('data-has-frame');
      this.switching = false;
    }
    async nextPath() {
      if (!this.abort || this.loading || this.failed) return;
      const native = this.config.hls && this.video.canPlayType('application/vnd.apple.mpegurl');
      const path = native && !this.tried.has('native') ? 'native'
        : this.config.hls && !this.tried.has('hlsjs') ? 'hlsjs'
        : this.config.mp4 && !this.tried.has('mp4') ? 'mp4' : '';
      this.clearSource();
      if (!path) {
        this.failed = true;
        this.path = '';
        this.setAttribute('data-playback', 'still');
        return;
      }
      this.path = path;
      this.tried.add(path);
      this.setAttribute('data-playback', path);
      this.loading = true;
      const generation = this.generation;
      try {
        if (path !== 'hlsjs') {
          const source = path === 'native' ? await this.adaptiveSource() : this.config.mp4;
          if (!this.abort || this.generation !== generation) return;
          this.video.src = source;
          this.loading = false;
          this.sync();
          return;
        }
        const Hls = await loadHls(this.config.library, this.abort.signal);
        if (!this.abort || this.generation !== generation) return;
        if (!Hls.isSupported()) throw new Error('HLS MSE unsupported');
        const source = await this.adaptiveSource();
        if (!this.abort || this.generation !== generation) return;
        // Native and HLS.js receive the same eligible renditions; adaptation stays upstream.
        const boundedPolicy = name => {
          const policy = Hls.DefaultConfig[name].default;
          const cap = retry => retry ? {...retry, maxNumRetry: Math.min(2, retry.maxNumRetry)} : null;
          return {default: {...policy, timeoutRetry: cap(policy.timeoutRetry), errorRetry: cap(policy.errorRetry)}};
        };
        this.hls = new Hls({autoStartLoad: false,
          fragLoadPolicy: boundedPolicy('fragLoadPolicy'),
          keyLoadPolicy: boundedPolicy('keyLoadPolicy')});
        const sourceEpoch = this.sourceEpoch;
        this.hls.on(Hls.Events.ERROR, (_, error) => { if (error.fatal && this.sourceEpoch === sourceEpoch) this.nextPath(); });
        this.hls.on(Hls.Events.MANIFEST_PARSED, () => this.sync());
        this.hls.loadSource(source);
        this.hls.attachMedia(this.video);
        this.loading = false;
        this.sync();
      } catch (_) {
        if (!this.abort || this.generation !== generation) return;
        this.loading = false;
        this.nextPath();
      }
    }
    disconnectedCallback() {
      if (!this.abort) return;
      this.generation++;
      this.abort.abort();
      this.abort = null;
      this.observer.disconnect();
      if (this.frameRequest && this.video.cancelVideoFrameCallback) this.video.cancelVideoFrameCallback(this.frameRequest);
      this.clearSource();
      if (this.masterUrl) URL.revokeObjectURL(this.masterUrl);
      this.masterUrl = null;
      this.masterPromise = null;
      this.loading = false;
      this.failed = false;
    }
  }
  customElements.define('rawry-hero-video', RawryHeroVideo);
})();
